import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';

import { SmtpConnectionApiService } from '../services/smtp-connection-api.service';
import { EmailInboxApiService } from '../services/email-inbox-api.service';
import { SmtpConnectionRequest, SmtpConnectionResponse } from '../models/email-inbox.model';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

/**
 * Conexión SMTP/IMAP del usuario (su propio servidor de correo, con app password).
 *
 * - SMTP: requerido — habilita el envío.
 * - IMAP: opcional — habilita la LECTURA de la bandeja de entrada (/correo).
 *
 * Nota compliance: la lectura vía Gmail API / Microsoft Graph fue eliminada;
 * IMAP sobre esta conexión es la única forma de leer el buzón.
 */
@Component({
  selector: 'app-smtp-connection-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    HasPermissionDirective
  ],
  templateUrl: './smtp-connection-form.component.html'
})
export class SmtpConnectionFormComponent implements OnInit {

  loading = false;
  saving = false;
  testing = false;
  deleting = false;
  error = '';

  /** Conexión ya registrada (null = aún sin conexión → modo alta). */
  existing: SmtpConnectionResponse | null = null;

  // inject() para poder usar fb en el inicializador del form (antes del constructor).
  private readonly fb = inject(FormBuilder);

  form = this.fb.nonNullable.group({
    // Envío (SMTP)
    mailAddress: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
    host: ['', [Validators.required, Validators.maxLength(255)]],
    port: [587, [Validators.required, Validators.min(1), Validators.max(65535)]],
    useTls: [true],
    useSsl: [false],
    username: ['', [Validators.maxLength(255)]],
    // El backend exige password en cada POST (incluso al editar): nunca vuelve en responses.
    password: ['', [Validators.required]],
    // Lectura (IMAP) — opcional
    imapHost: ['', [Validators.maxLength(255)]],
    imapPort: [993, [Validators.min(1), Validators.max(65535)]],
    imapUseSsl: [true]
  });

  constructor(
    private smtpApi: SmtpConnectionApiService,
    private inboxApi: EmailInboxApiService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  get isEditing(): boolean {
    return this.existing !== null;
  }

  get imapConfigurado(): boolean {
    return !!this.existing?.imapHost;
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.smtpApi.getMySmtp().subscribe({
      next: (conn) => {
        this.existing = conn;
        this.patchFromExisting(conn);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 404) {
          // Sin conexión aún: modo alta con defaults.
          this.existing = null;
        } else {
          this.error = err?.error?.message || 'Error al cargar la conexión de correo';
        }
      }
    });
  }

  private patchFromExisting(conn: SmtpConnectionResponse): void {
    this.form.patchValue({
      mailAddress: conn.mailAddress,
      host: conn.host,
      port: conn.port,
      useTls: conn.useTls ?? true,
      useSsl: conn.useSsl ?? false,
      username: conn.username ?? '',
      password: '',   // nunca vuelve del backend; debe reingresarse para guardar
      imapHost: conn.imapHost ?? '',
      imapPort: conn.imapPort ?? 993,
      imapUseSsl: conn.imapUseSsl ?? true
    });
  }

  // ── Helpers rápidos de proveedor ──────────────────────────────────

  prellenarGmail(): void {
    this.form.patchValue({
      host: 'smtp.gmail.com',
      port: 587,
      useTls: true,
      useSsl: false,
      imapHost: 'imap.gmail.com',
      imapPort: 993,
      imapUseSsl: true
    });
  }

  prellenarOutlook(): void {
    this.form.patchValue({
      host: 'smtp-mail.outlook.com',
      port: 587,
      useTls: true,
      useSsl: false,
      imapHost: 'outlook.office365.com',
      imapPort: 993,
      imapUseSsl: true
    });
  }

  // ── Validación ────────────────────────────────────────────────────

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getError(field: string): string | null {
    const control = this.form.get(field);
    if (!control || !control.errors) return null;
    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['email']) return 'Debe ser un correo válido';
    if (control.errors['maxlength']) return 'Excede la longitud máxima permitida';
    if (control.errors['min'] || control.errors['max']) return 'Debe estar entre 1 y 65535';
    return 'Valor inválido';
  }

  // ── Acciones ──────────────────────────────────────────────────────

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    const req: SmtpConnectionRequest = {
      mailAddress: v.mailAddress.trim(),
      host: v.host.trim(),
      port: v.port,
      useTls: v.useTls,
      useSsl: v.useSsl,
      password: v.password
    };
    const username = v.username.trim();
    if (username) req.username = username;

    const imapHost = v.imapHost.trim();
    if (imapHost) {
      req.imapHost = imapHost;
      req.imapPort = v.imapPort;
      req.imapUseSsl = v.imapUseSsl;
    }

    const wasEditing = this.isEditing;
    this.saving = true;
    this.error = '';

    this.smtpApi.registerSmtp(req).subscribe({
      next: (conn) => {
        this.saving = false;
        this.existing = conn;
        this.patchFromExisting(conn);
        if (wasEditing) {
          this.toast.updated('Conexión de correo', 'Se actualizó la conexión de correo correctamente.');
        } else {
          this.toast.created('Conexión de correo', 'Se registró la conexión de correo correctamente.');
        }
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.error?.message || 'Error al guardar la conexión de correo';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  /** Prueba la bandeja IMAP contra la conexión GUARDADA (no contra el form sin guardar). */
  probarBandeja(): void {
    this.testing = true;
    this.inboxApi.testConnection().subscribe({
      next: (resp) => {
        this.testing = false;
        if (resp.ok) {
          this.toast.success({
            title: 'Conexión exitosa',
            message: `Conexión exitosa, ${resp.sampleSize} mensaje${resp.sampleSize === 1 ? '' : 's'} encontrado${resp.sampleSize === 1 ? '' : 's'}.`
          });
        } else {
          this.toast.warning({ title: 'Conexión con problemas', message: 'La prueba no fue exitosa. Revisa tu configuración IMAP.' });
        }
      },
      error: (err) => {
        this.testing = false;
        this.toast.error({ title: 'Error', message: err?.error?.message || 'No se pudo probar la conexión IMAP' });
      }
    });
  }

  async eliminar(): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar conexión',
      message: 'Se eliminará tu conexión SMTP/IMAP. Dejarás de poder enviar correos con tu cuenta y de ver tu bandeja de entrada. ¿Continuar?'
    });
    if (!ok) return;

    this.deleting = true;
    this.smtpApi.deleteSmtp().subscribe({
      next: () => {
        this.deleting = false;
        this.existing = null;
        this.form.reset({
          mailAddress: '',
          host: '',
          port: 587,
          useTls: true,
          useSsl: false,
          username: '',
          password: '',
          imapHost: '',
          imapPort: 993,
          imapUseSsl: true
        });
        this.toast.deleted('Conexión de correo', 'Se eliminó la conexión de correo correctamente.');
      },
      error: (err) => {
        this.deleting = false;
        this.toast.error({ title: 'Error', message: err?.error?.message || 'Error al eliminar la conexión de correo' });
      }
    });
  }

  irABandeja(): void {
    this.router.navigate(['/correo']);
  }
}
